import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heartbeat, SignIn, X } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.2fr] bg-[#FAFBFC]" data-testid="login-page">
      <div className="flex items-center justify-center p-10">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-slate-900 flex items-center justify-center">
              <Heartbeat size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gray-500">ASC</div>
              <div className="font-heading text-xl font-bold text-slate-900 leading-tight">Credentialing</div>
            </div>
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mb-3">
            Sign in to your workspace
          </h1>
          <p className="text-sm text-gray-600 mb-10 leading-relaxed">
            Louisiana ambulatory surgery center credentialing — aligned with LA Medicaid,
            Joint Commission, and NAMSS ideal credentialing standards.
          </p>

          <div className="space-y-5">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1 w-full border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1 w-full border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-slate-900 text-white text-sm font-semibold uppercase tracking-wider py-3 hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <SignIn size={16} weight="bold" />
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="forgot-password-link"
                className="text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-slate-900 transition-colors"
              >
                Forgot password? · Text me a new one
              </button>
            </div>
          </div>
          <div className="mt-8 border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900" data-testid="hipaa-notice">
            <strong className="font-semibold">HIPAA Notice:</strong> This system processes Protected Health Information (PHI).
            Access is logged. Use only for authorized credentialing duties. By signing in you acknowledge your
            organization's privacy policies and your training obligations under 45 CFR §164.530.
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative border-l border-gray-200 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1725409796852-c0aa3aa61346?fm=jpg&q=85&w=2000&auto=format&fit=crop&ixlib=rb-4.1.0"
          alt="Close-up of a surgical OR light"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/40 to-transparent"></div>
        <div className="relative p-12 h-full flex flex-col justify-end text-white">
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase opacity-80 mb-3">
            LA Medicaid · Joint Commission · NAMSS
          </div>
          <h2 className="font-heading text-5xl font-bold tracking-tight leading-[1.05] max-w-lg">
            Credentialing, re-verified on a 36-month cadence.
          </h2>
          <p className="mt-6 text-sm leading-relaxed max-w-md opacity-90">
            A clinical-grade operational dashboard for ambulatory surgery centers.
            Scan documents, extract expirations, and keep every practitioner in good standing.
          </p>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}

function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="forgot-password-modal">
      <div className="bg-white border border-gray-200 w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="overline text-gray-500">Password reset</div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">Text me a new password</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-slate-900" data-testid="forgot-modal-close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-6 space-y-3 text-sm text-slate-900">
            <div className="bg-emerald-50 border-l-2 border-emerald-500 px-3 py-2 text-emerald-900">
              <strong>Check your phone.</strong>
            </div>
            <p className="text-gray-700">
              If <span className="font-mono">{email}</span> has an account on file with a cell phone, a new temporary
              password has just been texted via Twilio. Sign in with it and change it from your account.
            </p>
            <p className="text-xs text-gray-500">
              Didn't get a text within 1–2 minutes? Contact your credentialing coordinator — they may need to add a
              phone number to your record first.
            </p>
            <div className="flex justify-end pt-2 border-t border-gray-200">
              <button onClick={onClose} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700">Back to sign in</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-gray-700">
              Enter the email tied to your account. If we find a match and have a cell phone on file, we'll text you
              a new temporary password right away.
            </p>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                data-testid="forgot-email-input"
                autoFocus
              />
            </div>
            <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900">
              For security we never reveal whether an email exists or not. If you've entered the wrong email, you
              simply won't receive a text — and no one is told you tried.
            </div>
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
              <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Cancel</button>
              <button type="submit" disabled={sending} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-60" data-testid="forgot-submit">
                {sending ? "Sending…" : "Text me a new password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
